import { Section } from '~/interfaces';
import { getFirstNoteStartFromSections, getLastNoteEndFromSections } from '~/modules/songs/utils/notes-selectors';

interface Args {
  /** The sections of each track being sung. */
  tracks: Section[][];
  beatLength: number;
  gap: number;
  durationMs: number;
}

const beatToPosition = (beat: number, { beatLength, gap, durationMs }: Omit<Args, 'tracks'>) =>
  (beat * beatLength + gap) / durationMs;

/**
 * Where the singing starts and where it ends, and nothing else. A tick per sung section was true to
 * the frame, but the frame has three sections and a real song has thirty: at that density the bar
 * reads as a ruler instead of as "here is where you come in". Positions past the end of the video
 * are dropped rather than pinned to the edge, and two tracks that start together are one tick.
 */
export const getDurationTicks = ({ tracks, beatLength, gap, durationMs }: Args): number[] => {
  if (!durationMs) return [];

  const scale = { beatLength, gap, durationMs };
  const positions = new Set<number>();

  for (const sections of tracks) {
    for (const beat of [getFirstNoteStartFromSections(sections), getLastNoteEndFromSections(sections)]) {
      const position = beatToPosition(beat, scale);
      if (position < 0 || position > 1 || !Number.isFinite(position)) continue;

      positions.add(position);
    }
  }

  return [...positions].sort((one, other) => one - other);
};
